/**
 * Quantity-Based Discount Message System
 * Shows "Get X% OFF when you buy Y or more items" messages in cart and PDP
 * Mirrors Shopify Function logic exactly for consistency
 */

(function() {
  'use strict';

  // Get discount settings from theme config
  const getDiscountSettings = () => window.theme?.settings?.quantityDiscount || null;

  /**
   * Filter eligible cart items (mirrors Shopify Function filtering logic)
   * Excludes: Gift Cards and Shipping Protection items
   * @param {Object} cart - Cart object from Shopify Cart API
   * @returns {Array} - Filtered array of eligible items
   */
  function filterEligibleLines(cart) {
    if (!cart || !cart.items) return [];

    return cart.items.filter(item => {
      // Exclude gift cards by product type
      if (item.product_type === 'Gift Card') {
        console.log('[Quantity Discount] Excluding gift card:', item.product_title);
        return false;
      }

      // Exclude shipping protection by title
      const title = (item.product_title || '').toLowerCase();
      if (title.includes('free returns') ||
          title.includes('package protection') ||
          title.includes('shipping protection')) {
        console.log('[Quantity Discount] Excluding shipping protection:', item.product_title);
        return false;
      }

      return true;
    });
  }

  /**
   * Calculate total quantity of eligible items
   * @param {Object} cart - Cart object from Shopify Cart API
   * @returns {Number} - Total quantity of eligible items
   */
  function getTotalEligibleQuantity(cart) {
    const eligibleLines = filterEligibleLines(cart);
    const total = eligibleLines.reduce((sum, item) => sum + item.quantity, 0);
    console.log('[Quantity Discount] Total eligible quantity:', total);
    return total;
  }

  /**
   * Update cart drawer message
   * @param {Number} totalQuantity - Total eligible items in cart
   */
  function updateCartMessage(totalQuantity) {
    const settings = getDiscountSettings();
    if (!settings || !settings.enabled) return;

    const container = document.getElementById('quantity-discount-message');
    if (!container) return;

    const threshold = settings.minimumQuantity;
    const discount = settings.discountPercentage;
    const highlightColor = settings.cartHighlightColor || '#3ea36a';

    let messageHTML = '';
    let isQualified = false;

    if (totalQuantity >= threshold) {
      // Qualified - customer has enough items
      isQualified = true;
      messageHTML = `
        <div class="quantity-discount__content quantity-discount--qualified">
          <div class="quantity-discount__icon">✓</div>
          <div class="quantity-discount__text">
            <strong style="color: ${highlightColor};">You qualify for ${discount}% OFF RRP!</strong>
            <span class="quantity-discount__subtext">Discount applied at checkout</span>
          </div>
        </div>
      `;
    } else {
      // Not qualified - show how many more needed
      const needed = threshold - totalQuantity;
      const itemText = needed === 1 ? 'item' : 'items';
      messageHTML = `
        <div class="quantity-discount__content quantity-discount--not-qualified">
          <div class="quantity-discount__icon">🎁</div>
          <div class="quantity-discount__text">
            Add <strong style="color: ${highlightColor};">${needed} more ${itemText}</strong> to get
            <strong style="color: ${highlightColor};">${discount}% OFF</strong> the RRP value of your order
          </div>
        </div>
      `;
    }

    container.innerHTML = messageHTML;
    container.classList.toggle('is-qualified', isQualified);
    container.classList.toggle('is-visible', true);
  }

  /**
   * Update PDP banner message
   * Shows message if adding the current product would unlock the discount
   */
  async function updatePDPBanner() {
    const settings = getDiscountSettings();
    if (!settings || !settings.enabled) return;

    const banner = document.getElementById('quantity-discount-banner');
    if (!banner) return;

    const productData = document.getElementById('product-data');
    if (!productData) return;

    try {
      // Fetch current cart
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const currentQuantity = getTotalEligibleQuantity(cart);
      const threshold = settings.minimumQuantity;
      const discount = settings.discountPercentage;
      const highlightColor = settings.pdpHighlightColor || '#3ea36a';
      const bgColor = settings.pdpBgColor || '#FAC215';
      const borderColor = settings.pdpBorderColor || '#e49a04';

      // Only show banner if:
      // 1. Customer doesn't already qualify, AND
      // 2. Adding this product would reach threshold
      if (currentQuantity < threshold && (currentQuantity + 1) >= threshold) {
        const messageHTML = `Add this product to get <span style="color: ${highlightColor};">${discount}% OFF RRP</span>`;
        banner.innerHTML = messageHTML;
        banner.classList.add('show');
        banner.style.visibility = 'visible';
        banner.style.position = 'static';
        banner.style.backgroundColor = bgColor;
        banner.style.borderColor = borderColor;
      } else {
        banner.classList.remove('show');
        banner.style.visibility = 'hidden';
        banner.style.position = 'absolute';
        banner.innerHTML = '';
      }
    } catch (error) {
      console.error('[Quantity Discount] Error updating PDP banner:', error);
    }
  }

  /**
   * Handle cart updates
   * Fetches latest cart and updates all messages
   */
  async function handleCartUpdate() {
    const settings = getDiscountSettings();
    if (!settings || !settings.enabled) return;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const totalQuantity = getTotalEligibleQuantity(cart);
      updateCartMessage(totalQuantity);

      // Also update PDP banner if on product page
      if (document.getElementById('quantity-discount-banner')) {
        updatePDPBanner();
      }
    } catch (error) {
      console.error('[Quantity Discount] Error fetching cart:', error);
    }
  }

  /**
   * Initialize quantity discount system
   */
  function init() {
    const settings = getDiscountSettings();
    if (!settings || !settings.enabled) {
      console.log('[Quantity Discount] Not enabled');
      return;
    }

    console.log('[Quantity Discount] Initializing...', settings);

    // Initial update
    handleCartUpdate();

    // Listen to cart update events
    document.addEventListener('on:cart:after-merge', function(evt) {
      console.log('[Quantity Discount] Cart updated event received');
      handleCartUpdate();
    });

    // Listen to variant change on PDP (to update banner)
    document.addEventListener('on:variant:change', function(evt) {
      if (document.getElementById('quantity-discount-banner')) {
        updatePDPBanner();
      }
    });

    console.log('[Quantity Discount] Initialized successfully');
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose functions for debugging
  window.quantityDiscount = {
    update: handleCartUpdate,
    filterEligibleLines: filterEligibleLines,
    getTotalEligibleQuantity: getTotalEligibleQuantity
  };

})();
